import { Link } from "react-router-dom";
import { Wrench, Phone, Clock, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import { SITE } from "../config/site";
import { LOCATIONS } from "../data/locations";

export const TikTokIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export const SOCIAL_ICONS = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
  { key: "youtube", label: "YouTube", Icon: Youtube },
];

export function Footer() {
  return (
    <footer className="border-t border-edge bg-slate2 pb-24 lg:pb-0">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-crimson shadow-md shadow-crimson/20">
              <Wrench className="h-5 w-5 text-white" />
            </span>
            <span className="flex items-baseline gap-1.5">
              <span className="font-display text-xl font-extrabold uppercase tracking-tight text-slate-50">
                My Mobile <span className="text-crimson">Mechanic</span>
              </span>
              <span className="rounded-md bg-crimson/10 px-1.5 py-0.5 font-mono text-[11px] font-bold tracking-wider text-crimson">
                978
              </span>
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Professional mobile brake replacement and automotive repair at your home or workplace.
          </p>
          <div className="mt-5 flex gap-3">
            {SOCIAL_ICONS.map(({ key, label, Icon }) => (
              <a
                key={key}
                href={SITE.socials[key]}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${SITE.name} on ${label}`}
                data-testid={`footer-social-${key}`}
                className="flex h-11 w-11 items-center justify-center border border-edge text-slate-300 transition-colors hover:border-crimson hover:text-crimson"
              >
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-slate-50">Contact & Hours</h3>
          <ul className="mt-4 space-y-3 text-sm text-slate-400">
            <li>
              <a href={SITE.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-2 text-slate-200 transition-colors hover:text-amber2">
                <Phone className="h-4 w-4 text-crimson" /> {SITE.phone}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-crimson" /> {SITE.hours} · {SITE.closedNote}
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-crimson" />
              <span>Serving {SITE.serviceArea}. Mobile service — we come to you.</span>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-slate-50">Quick Links</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[
              { to: "/get-a-brake-quote", label: "Get a Brake Quote", testid: "footer-quote-link" },
              { href: true, label: "Call or Text to Book", testid: "footer-book-link" },
              { to: "/brake-replacement", label: "Brake Replacement", testid: "footer-brakes-link" },
              { to: "/reviews", label: "Customer Reviews", testid: "footer-reviews-link" },
              { to: "/warranty", label: "Warranty Information", testid: "footer-warranty-link" },
              { to: "/privacy", label: "Privacy Policy", testid: "footer-privacy-link" },
              { to: "/terms", label: "Terms of Service", testid: "footer-terms-link" },
            ].map((l) => (
              <li key={l.testid}>
                {l.href ? (
                  <a href={SITE.phoneHref} data-testid={l.testid} className="text-slate-400 transition-colors hover:text-amber2">
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} data-testid={l.testid} className="text-slate-400 transition-colors hover:text-amber2">
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-slate-50">Service Areas</h3>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {LOCATIONS.map((l) => (
              <li key={l.slug}>
                <Link
                  to={`/${l.slug}`}
                  data-testid={`footer-location-${l.city.toLowerCase()}-link`}
                  className="text-slate-400 transition-colors hover:text-amber2"
                >
                  {l.city}, {l.st}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-edge">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-slate-500 sm:flex-row sm:px-6">
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <p>Mobile brake replacement · Middlesex County, MA & Southern NH</p>
        </div>
      </div>
    </footer>
  );
}
